import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  Image,
  Modal,
  Row,
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheckCircle,
  faComments,
  faFilePdf,
  faFileSignature,
  faLightbulb,
  faPaperPlane,
  faSave,
  faSignature,
  faSms,
  faSpinner,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';

import useDbqForm from '../../hooks/useDbqForm';
import { useUser } from '../../contexts/UserContext';
import {
  DbqCommentsPanel,
  DbqFormView,
  dbqStandaloneStyles,
  dbqStyles,
} from './dbq';
import MoPanel from './dbq/MoPanel';
import {
  useFetchCurrentProviderProfile,
  useFetchUserDbqProviders,
  useGenerateDbqPdf,
  useSendDbqEmail,
  useSignDbq,
} from '../../services/userDbq';

const normalizeDbqTextForCompare = (value) => {
  return String(value || '')
    .toLowerCase()
    .replace(/[’]/g, "'")
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
};

const isExaminerCertificationAndSignatureSection = (section) => {
  const label = normalizeDbqTextForCompare(section?.label);
  return (
    label.includes('examiner') &&
    label.includes('certification') &&
    label.includes('signature')
  );
};

const findRemarksFieldIdInElements = (elements = []) => {
  for (const element of elements) {
    if (element?.type === 'field' && element?.id) {
      const idLower = String(element.id).toLowerCase();
      const labelLower = String(element.label || '').toLowerCase();
      if (
        idLower.includes('remarks') ||
        idLower.includes('remark') ||
        labelLower.includes('remarks')
      ) {
        return element.id;
      }
    }

    if (element?.elements?.length) {
      const nested = findRemarksFieldIdInElements(element.elements);
      if (nested) return nested;
    }

    if (Array.isArray(element?.options)) {
      for (const option of element.options) {
        const nested = findRemarksFieldIdInElements(
          option.child_elements || [],
        );
        if (nested) return nested;
      }
    }
  }

  return null;
};

const LegacyUserDbqEditor = () => {
  const { userDbqId } = useParams();
  const { user, hasPermission, hasRole } = useUser();
  const queryClient = useQueryClient();
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [activeCommentFieldId, setActiveCommentFieldId] = useState(null);
  const [activeCommentFieldLabel, setActiveCommentFieldLabel] = useState('');
  const [showSendModal, setShowSendModal] = useState(false);
  const [selectedProviderId, setSelectedProviderId] = useState('');
  const [includeComments, setIncludeComments] = useState(false);
  const [sendSms, setSendSms] = useState(false);
  const [showSigningModal, setShowSigningModal] = useState(false);
  const [showMoPanel, setShowMoPanel] = useState(false);
  const [certificationData, setCertificationData] = useState({
    signature: '',
    nameAndTitle: '',
    specialty: '',
    phoneOrFax: '',
    npi: '',
    licenseNumberAndState: '',
    address: '',
    dateSigned: new Date().toISOString().split('T')[0],
  });
  const [selectedProfileId, setSelectedProfileId] = useState(null);
  const saveTimeoutRef = useRef(null);

  const isAdmin = hasPermission('admin:dbq_forms');
  const isProvider = hasRole('provider');
  const canAccessMoChat = hasPermission('admin:dbq_forms');

  const { data: providersData } = useFetchUserDbqProviders(
    isAdmin ? userDbqId : null,
  );
  const providers = useMemo(
    () => providersData?.providers || [],
    [providersData],
  );

  const providerOptions = useMemo(() => {
    return providers.map((provider) => ({
      userId: provider.userId,
      email: provider.userEmail,
      phone: provider.userPhone || null,
      name:
        `${provider.userFirstName || ''} ${provider.userLastName || ''}`.trim() ||
        provider.userEmail,
    }));
  }, [providers]);

  const selectedProvider = useMemo(() => {
    return providerOptions.find(
      (provider) => provider.userId === selectedProviderId,
    );
  }, [providerOptions, selectedProviderId]);

  const sendEmailMutation = useSendDbqEmail(
    () => {
      const message =
        sendSms && selectedProvider?.phone
          ? 'Email and text message sent to provider successfully!'
          : 'Email sent to provider successfully!';
      toast.success(message);
      setShowSendModal(false);
      setSelectedProviderId('');
      setIncludeComments(false);
      setSendSms(false);
    },
    (requestError) => {
      toast.error(requestError?.response?.data?.msg || 'Failed to send email');
    },
  );

  const { data: providerProfileData } =
    useFetchCurrentProviderProfile(isProvider);
  const providerProfile = isProvider ? providerProfileData?.provider : null;
  const providerProfiles = useMemo(
    () => providerProfile?.profiles || [],
    [providerProfile],
  );

  useEffect(() => {
    if (!providerProfile?.providerSignatureUrl) return;
    const image = new window.Image();
    image.src = providerProfile.providerSignatureUrl;
  }, [providerProfile?.providerSignatureUrl]);

  useEffect(() => {
    if (!providerProfile || providerProfiles.length === 0) return;

    const defaultProfile =
      providerProfiles.find((profile) => profile.isDefault) ||
      providerProfiles[0];
    setSelectedProfileId(defaultProfile.profileId);

    const fullName =
      `${providerProfile.userFirstName || ''} ${providerProfile.userLastName || ''}`.trim();
    const nameWithTitle = defaultProfile.title
      ? `${fullName}, ${defaultProfile.title}`
      : fullName;
    const licenseInfo =
      defaultProfile.licenseNumber && defaultProfile.licenseState
        ? `${defaultProfile.licenseNumber} — ${defaultProfile.licenseState}`
        : defaultProfile.licenseNumber || '';

    setCertificationData({
      signature: fullName,
      nameAndTitle: nameWithTitle,
      specialty: defaultProfile.specialty || '',
      phoneOrFax:
        defaultProfile.businessPhone || providerProfile.userPhone || '',
      npi: defaultProfile.npi || '',
      licenseNumberAndState: licenseInfo,
      address: defaultProfile.address || '',
      dateSigned: new Date().toISOString().split('T')[0],
    });
  }, [providerProfile, providerProfiles]);

  const signDbqMutation = useSignDbq(
    () => {
      toast.success(
        'DBQ signed and submitted successfully! The claim manager has been notified.',
      );
      setShowSigningModal(false);
      queryClient.invalidateQueries({ queryKey: ['userDbq', userDbqId] });
    },
    (requestError) => {
      toast.error(requestError?.response?.data?.msg || 'Failed to sign DBQ');
    },
  );

  const generatePdfMutation = useGenerateDbqPdf(
    () => {
      toast.success('PDF generated successfully!');
      queryClient.invalidateQueries({ queryKey: ['userDbq', userDbqId] });
      queryClient.invalidateQueries({ queryKey: ['user-dbqs'] });
    },
    (requestError) => {
      toast.error(
        requestError?.response?.data?.msg || 'Failed to generate PDF',
      );
    },
  );

  const formContext = useDbqForm();
  const {
    formData,
    setFormData,
    repeatingInstances,
    initializeRepeatingInstances,
    filterFormDataByActiveInstances,
    filterHiddenFieldValues,
    buildRenderWithGroupMap,
    setFormJson,
    handleFieldChange,
    getFieldComments,
    addComment,
    addReply,
    resolveComment,
    getAllActiveCommentFieldIds,
    getFieldCommentCount,
    getMissingRequiredFields,
  } = formContext;

  const { data, isLoading, error } = useQuery({
    queryKey: ['userDbq', userDbqId],
    queryFn: async () => {
      const response = await axios.get(`/dbq/user-dbq/${userDbqId}`);
      return response.data;
    },
    refetchOnWindowFocus: false,
  });

  const userDbq = data?.userDbq;
  const rawFormJson = userDbq?.formJson;
  const requiredFields = useMemo(
    () => userDbq?.requiredFields || [],
    [userDbq?.requiredFields],
  );

  // Signed state (2026-08-04 rework): signing no longer locks editing - the
  // server records every post-sign change instead. Legacy DBQs have no
  // request workflow, so there is no finalize lock here either.
  const providerSigned =
    Boolean(formData?._certification?.signedAt) ||
    userDbq?.status === 'complete';

  const formJson = useMemo(() => {
    if (!rawFormJson?.sections?.length) return rawFormJson;

    const lastSection = rawFormJson.sections[rawFormJson.sections.length - 1];
    if (!isExaminerCertificationAndSignatureSection(lastSection))
      return rawFormJson;

    return {
      ...rawFormJson,
      sections: rawFormJson.sections.slice(0, -1),
    };
  }, [rawFormJson]);

  useEffect(() => {
    if (formJson) {
      setFormJson(formJson);
    }
  }, [formJson, setFormJson]);

  const renderWithGroupMap = useMemo(
    () => (formJson ? buildRenderWithGroupMap(formJson) : {}),
    [formJson, buildRenderWithGroupMap],
  );

  const activeComments = activeCommentFieldId
    ? getFieldComments(activeCommentFieldId)
    : [];
  const commentFieldIds = useMemo(
    () => getAllActiveCommentFieldIds(formJson),
    [getAllActiveCommentFieldIds, formJson],
  );
  const totalCommentCount = useMemo(() => {
    if (!commentFieldIds?.length) return 0;
    if (typeof getFieldCommentCount !== 'function')
      return commentFieldIds.length;
    return commentFieldIds.reduce(
      (sum, fieldId) => sum + (getFieldCommentCount(fieldId) || 0),
      0,
    );
  }, [commentFieldIds, getFieldCommentCount]);

  const getFieldLabelByKey = useCallback(
    (fieldKey) => {
      const bareFieldId = fieldKey.includes('__')
        ? fieldKey.split('__').pop()
        : fieldKey;
      let fieldLabel = bareFieldId;

      const findLabel = (elements) => {
        for (const element of elements || []) {
          if (element.id === bareFieldId) {
            fieldLabel = element.label || bareFieldId;
            return true;
          }
          if (element.elements && findLabel(element.elements)) return true;
          if (element.options) {
            for (const option of element.options) {
              if (option.child_elements && findLabel(option.child_elements))
                return true;
            }
          }
        }
        return false;
      };

      formJson?.sections?.forEach((section) => findLabel(section.elements));
      return fieldLabel;
    },
    [formJson],
  );

  const openCommentPanel = useCallback(
    (fieldId, fieldLabel) => {
      setActiveCommentFieldId(fieldId);
      setActiveCommentFieldLabel(fieldLabel || getFieldLabelByKey(fieldId));

      setTimeout(() => {
        const fieldElement = document.querySelector(
          `[data-field-id="${fieldId}"]`,
        );
        if (fieldElement) {
          fieldElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    },
    [getFieldLabelByKey],
  );

  const handleCommentNavigate = useCallback(
    (fieldId) => {
      openCommentPanel(fieldId, getFieldLabelByKey(fieldId));
    },
    [getFieldLabelByKey, openCommentPanel],
  );

  const handleCloseComments = useCallback(() => {
    setActiveCommentFieldId(null);
    setActiveCommentFieldLabel('');
  }, []);

  const currentUserInfo = useMemo(
    () => ({
      userId: user?.userId,
      name:
        user?.userFirstName && user?.userLastName
          ? `${user.userFirstName} ${user.userLastName}`
          : user?.userEmail || 'Unknown User',
    }),
    [user],
  );

  const canResolveComment = useCallback(
    (comment) => {
      if (isAdmin || isProvider) return true;
      return (
        String(comment?.author?.userId || '') === String(user?.userId || '')
      );
    },
    [isAdmin, isProvider, user?.userId],
  );

  useEffect(() => {
    if (userDbq?.formData) {
      setFormData(userDbq.formData);
    }
  }, [userDbq, setFormData]);

  useEffect(() => {
    if (formJson) {
      initializeRepeatingInstances(formJson, userDbq?.formData || {});
    }
  }, [formJson, initializeRepeatingInstances, userDbq?.formData]);

  const handleSave = useCallback(
    async (isAutoSave = false) => {
      try {
        if (!isAutoSave) setIsSaving(true);

        let cleanedFormData = filterFormDataByActiveInstances(
          formData,
          repeatingInstances,
        );
        cleanedFormData = filterHiddenFieldValues(cleanedFormData, formJson);

        await axios.put(`/dbq/user-dbq/${userDbqId}`, {
          formData: cleanedFormData,
        });

        queryClient.setQueryData(['userDbq', userDbqId], (previous) =>
          previous
            ? {
                ...previous,
                userDbq: {
                  ...previous.userDbq,
                  formData: cleanedFormData,
                },
              }
            : previous,
        );

        if (!isAutoSave) {
          setShowSaveSuccess(true);
          setTimeout(() => setShowSaveSuccess(false), 2500);
        }
        return true;
      } catch (requestError) {
        console.error('Error saving legacy DBQ:', requestError);
        if (!isAutoSave) {
          toast.error(
            requestError?.response?.data?.msg || 'Failed to save DBQ',
          );
        }
        return false;
      } finally {
        if (!isAutoSave) setIsSaving(false);
      }
    },
    [
      filterFormDataByActiveInstances,
      filterHiddenFieldValues,
      formData,
      formJson,
      queryClient,
      repeatingInstances,
      userDbqId,
    ],
  );

  useEffect(() => {
    if (!userDbqId) return undefined;

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(() => {
      handleSave(true);
    }, 2000);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [formData, handleSave, userDbqId]);

  const handleSendSubmit = () => {
    if (!selectedProvider) {
      toast.error('Please select a provider');
      return;
    }

    sendEmailMutation.mutate({
      userDbqId,
      recipientEmail: selectedProvider.email,
      includeComments,
      sendSms: sendSms && !!selectedProvider.phone,
      recipientPhone: selectedProvider.phone,
    });
  };

  const handleGeneratePdf = useCallback(() => {
    handleSave(false).then((saveSuccessful) => {
      if (saveSuccessful) {
        generatePdfMutation.mutate(userDbqId);
      }
    });
  }, [generatePdfMutation, handleSave, userDbqId]);

  const handleSignAndSubmit = useCallback(() => {
    const missingFields = getMissingRequiredFields(formJson, requiredFields);

    if (missingFields.length > 0) {
      const fieldLabels = missingFields
        .map((field) => field.label)
        .join('\n• ');
      window.alert(
        `The following required fields still need answers:\n\n• ${fieldLabels}`,
      );
      return;
    }

    setShowSigningModal(true);
  }, [formJson, getMissingRequiredFields, requiredFields]);

  const handleCertificationChange = useCallback((event) => {
    const { name, value } = event.target;
    setCertificationData((previous) => ({ ...previous, [name]: value }));
  }, []);

  const handleProfileSelect = useCallback(
    (profileId) => {
      const profile = providerProfiles.find(
        (item) => item.profileId === profileId,
      );
      if (!profile) return;

      setSelectedProfileId(profileId);

      const fullName = providerProfile
        ? `${providerProfile.userFirstName || ''} ${providerProfile.userLastName || ''}`.trim()
        : '';
      const nameWithTitle = profile.title
        ? `${fullName}, ${profile.title}`
        : fullName;
      const licenseInfo =
        profile.licenseNumber && profile.licenseState
          ? `${profile.licenseNumber} — ${profile.licenseState}`
          : profile.licenseNumber || '';

      setCertificationData((previous) => ({
        ...previous,
        signature: fullName,
        nameAndTitle: nameWithTitle,
        specialty: profile.specialty || '',
        phoneOrFax: profile.businessPhone || providerProfile?.userPhone || '',
        npi: profile.npi || '',
        licenseNumberAndState: licenseInfo,
        address: profile.address || '',
      }));
    },
    [providerProfile, providerProfiles],
  );

  const handleSignSubmit = useCallback(() => {
    handleSave(false).then((saveSuccessful) => {
      if (saveSuccessful) {
        signDbqMutation.mutate({
          userDbqId,
          certificationData,
        });
      }
    });
  }, [certificationData, handleSave, signDbqMutation, userDbqId]);

  const handleApplyMoToDbq = useCallback(
    (medicalOpinionText, fieldId) => {
      if (!medicalOpinionText) return;

      const targetFieldId =
        fieldId ||
        findRemarksFieldIdInElements(
          formJson?.sections?.flatMap((section) => section.elements) || [],
        ) ||
        'remarks';
      setFormData((previous) => ({
        ...(previous || {}),
        [targetFieldId]: medicalOpinionText,
      }));
    },
    [formJson?.sections, setFormData],
  );

  useEffect(() => {
    if (canAccessMoChat) return;
    setShowMoPanel(false);
  }, [canAccessMoChat]);

  if (isLoading) {
    return (
      <div className="dbq-renderer">
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: '100vh' }}
        >
          <div className="text-center">
            <FontAwesomeIcon
              icon={faSpinner}
              spin
              size="3x"
              className="text-primary mb-3"
            />
            <p className="text-muted">Loading form...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dbq-renderer">
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: '100vh' }}
        >
          <Alert variant="danger">
            <strong>Error:</strong> Failed to load form.{' '}
            {error.response?.data?.msg || error.message}
          </Alert>
        </div>
      </div>
    );
  }

  const headerStyles = `
    .legacy-dbq-page {
      background: #f5f7fb;
      min-height: 100vh;
    }

    .legacy-dbq-toolbar {
      position: sticky;
      top: 0;
      z-index: 1000;
      background: rgba(255, 255, 255, 0.96);
      backdrop-filter: blur(10px);
      border-bottom: 1px solid rgba(15, 23, 42, 0.08);
      padding: 1rem 1.5rem;
      margin-bottom: 1.5rem;
    }

    .legacy-dbq-toolbar-inner {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 1rem;
      max-width: 1600px;
      margin: 0 auto;
    }

    .legacy-dbq-toolbar-actions {
      display: flex;
      gap: 0.75rem;
      flex-wrap: wrap;
      justify-content: flex-end;
    }

    .legacy-dbq-shell {
      max-width: 1600px;
      margin: 0 auto;
      padding: 0 1.5rem 1.5rem;
    }

    .legacy-dbq-form-card {
      border: none;
      box-shadow: 0 12px 30px rgba(15, 23, 42, 0.08);
      border-radius: 18px;
    }

    .legacy-dbq-comment-alert {
      border: 1px solid rgba(245, 158, 11, 0.2);
      background: rgba(254, 243, 199, 0.65);
    }

    @media (max-width: 768px) {
      .legacy-dbq-toolbar {
        padding: 0.875rem 1rem;
      }

      .legacy-dbq-shell {
        padding: 0 1rem 1rem;
      }

      .legacy-dbq-toolbar-inner {
        flex-direction: column;
        align-items: stretch;
      }

      .legacy-dbq-toolbar-actions {
        justify-content: stretch;
      }

      .legacy-dbq-toolbar-actions > * {
        flex: 1 1 auto;
      }
    }
  `;

  return (
    <>
      <style>{dbqStyles}</style>
      <style>{dbqStandaloneStyles}</style>
      <style>{headerStyles}</style>

      <div className="legacy-dbq-page">
        <div className="legacy-dbq-toolbar">
          <div className="legacy-dbq-toolbar-inner">
            <div>
              <h1 className="h4 mb-1">
                {formJson?.title || userDbq?.title || 'DBQ Form'}
              </h1>
              <div className="text-muted small">
                {showSaveSuccess ? (
                  <>
                    <FontAwesomeIcon
                      icon={faCheckCircle}
                      className="me-2 text-success"
                    />
                    Saved
                  </>
                ) : (
                  'Auto-saving as you type'
                )}
              </div>
            </div>
            <div className="legacy-dbq-toolbar-actions">
              {canAccessMoChat ? (
                <Button
                  variant={showMoPanel ? 'dark' : 'outline-dark'}
                  onClick={() => setShowMoPanel((previous) => !previous)}
                >
                  <FontAwesomeIcon icon={faLightbulb} className="me-2" />
                  {showMoPanel ? 'Hide MO' : 'Medical Opinion'}
                </Button>
              ) : null}
              <Button
                variant="outline-primary"
                onClick={() => handleSave(false)}
                disabled={isSaving}
              >
                {isSaving ? (
                  <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                ) : (
                  <FontAwesomeIcon icon={faSave} className="me-2" />
                )}
                Save
              </Button>
              {isAdmin ? (
                <Button
                  variant="outline-success"
                  onClick={() => setShowSendModal(true)}
                  disabled={sendEmailMutation.isPending}
                >
                  {sendEmailMutation.isPending ? (
                    <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                  ) : (
                    <FontAwesomeIcon icon={faPaperPlane} className="me-2" />
                  )}
                  Send to Provider
                </Button>
              ) : null}
              {isAdmin ? (
                <Button
                  variant="outline-danger"
                  onClick={handleGeneratePdf}
                  disabled={generatePdfMutation.isPending}
                >
                  {generatePdfMutation.isPending ? (
                    <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                  ) : (
                    <FontAwesomeIcon icon={faFilePdf} className="me-2" />
                  )}
                  Generate PDF
                </Button>
              ) : null}
              {isProvider ? (
                <Button variant="primary" onClick={handleSignAndSubmit}>
                  <FontAwesomeIcon icon={faFileSignature} className="me-2" />
                  Sign & Submit
                </Button>
              ) : null}
            </div>
          </div>
        </div>

        <div className="legacy-dbq-shell">
          <div
            className={`dbq-layout-wrapper ${activeCommentFieldId ? 'has-comments' : ''}`}
          >
            <div className="dbq-form-container">
              <Card className="legacy-dbq-form-card">
                <Card.Body className="p-4 p-md-5">
                  {formJson?.description ? (
                    <p className="text-muted mb-4">{formJson.description}</p>
                  ) : null}

                  {totalCommentCount > 0 && !activeCommentFieldId ? (
                    <Alert className="legacy-dbq-comment-alert d-flex align-items-center justify-content-between mb-4">
                      <div>
                        <FontAwesomeIcon
                          icon={faComments}
                          className="me-2 text-warning"
                        />
                        {totalCommentCount} unresolved comment
                        {totalCommentCount === 1 ? '' : 's'} on this form.
                      </div>
                      <Button
                        variant="outline-dark"
                        size="sm"
                        onClick={() =>
                          handleCommentNavigate(commentFieldIds[0])
                        }
                      >
                        Review comments
                      </Button>
                    </Alert>
                  ) : null}

                  {providerSigned ? (
                    <Alert variant="warning" className="py-2">
                      <strong>
                        Signed by{' '}
                        {formData?._certification?.providerName ||
                          'the provider'}
                        {formData?._certification?.signedAt
                          ? ` on ${new Date(formData._certification.signedAt).toLocaleDateString()}`
                          : ''}
                        .
                      </strong>{' '}
                      Edits remain open and every change is recorded for QA
                      review.
                    </Alert>
                  ) : null}

                  <Form>
                    <DbqFormView
                      formJson={formJson}
                      formContext={{
                        ...formContext,
                        handleFieldChange,
                      }}
                      renderWithGroupMap={renderWithGroupMap}
                      isAdmin={isAdmin}
                      canComment
                      readOnly={false}
                      onCommentClick={openCommentPanel}
                      activeCommentFieldId={activeCommentFieldId}
                      requiredFields={requiredFields}
                    />
                  </Form>
                </Card.Body>
              </Card>

              {showMoPanel && canAccessMoChat ? (
                <Card className="legacy-dbq-form-card mt-4">
                  <Card.Body className="p-0">
                    <MoPanel
                      userDbq={userDbq}
                      onApplyToDbq={handleApplyMoToDbq}
                      onCollapse={() => setShowMoPanel(false)}
                    />
                  </Card.Body>
                </Card>
              ) : null}
            </div>

            <div
              className={`dbq-comments-sidebar ${activeCommentFieldId ? 'visible' : ''}`}
            >
              {activeCommentFieldId ? (
                <DbqCommentsPanel
                  comments={activeComments}
                  fieldId={activeCommentFieldId}
                  fieldLabel={activeCommentFieldLabel}
                  canComment
                  canResolve={isAdmin || isProvider}
                  canResolveComment={canResolveComment}
                  currentUser={currentUserInfo}
                  onResolve={resolveComment}
                  onReply={addReply}
                  onAddComment={addComment}
                  onNavigate={handleCommentNavigate}
                  commentFieldIds={commentFieldIds}
                  onClose={handleCloseComments}
                />
              ) : null}
            </div>
          </div>
        </div>
      </div>

      <Modal show={showSendModal} onHide={() => setShowSendModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Send DBQ to Provider</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Select Provider</Form.Label>
            {providerOptions.length === 0 ? (
              <Alert variant="warning" className="mb-0">
                No providers are assigned to this claim. Please assign a
                provider first.
              </Alert>
            ) : (
              <Form.Select
                value={selectedProviderId}
                onChange={(event) => setSelectedProviderId(event.target.value)}
              >
                <option value="">-- Select a provider --</option>
                {providerOptions.map((provider) => (
                  <option key={provider.userId} value={provider.userId}>
                    {provider.name} ({provider.email})
                  </option>
                ))}
              </Form.Select>
            )}
            <Form.Text className="text-muted">
              The provider will receive an email with a link to complete the
              form.
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              id="legacy-include-comments-checkbox"
              label="Include comments/notes in email"
              checked={includeComments}
              onChange={(event) => setIncludeComments(event.target.checked)}
            />
            <Form.Text className="text-muted">
              If checked, any unresolved comments on the form will be included
              in the email body.
            </Form.Text>
          </Form.Group>

          <Form.Group>
            <Form.Check
              type="checkbox"
              id="legacy-send-sms-checkbox"
              label={
                <>
                  <FontAwesomeIcon icon={faSms} className="me-1" />
                  Also send a text message
                </>
              }
              checked={sendSms}
              onChange={(event) => setSendSms(event.target.checked)}
              disabled={!selectedProvider?.phone}
            />
            <Form.Text className="text-muted">
              {selectedProvider?.phone
                ? 'The provider will also receive an SMS notification with the form link.'
                : selectedProviderId
                  ? 'This provider does not have a phone number on file.'
                  : 'Select a provider to enable SMS notification.'}
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowSendModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSendSubmit}
            disabled={
              sendEmailMutation.isPending ||
              providerOptions.length === 0 ||
              !selectedProviderId
            }
          >
            {sendEmailMutation.isPending ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                Sending...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faPaperPlane} className="me-2" />
                Send to Provider
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showSigningModal}
        onHide={() => setShowSigningModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faSignature} className="me-2 text-success" />
            Sign & Submit DBQ
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="info">
            <strong>Certification Statement:</strong>
            <p className="mb-0 mt-2">
              To the best of my knowledge, the information contained herein is
              accurate, complete, and current.
            </p>
          </Alert>

          {providerProfiles.length > 0 ? (
            <Form.Group className="mb-4">
              <Form.Label>Certification Profile</Form.Label>
              <Form.Select
                value={selectedProfileId || ''}
                onChange={(event) => handleProfileSelect(event.target.value)}
              >
                {providerProfiles.map((profile) => (
                  <option key={profile.profileId} value={profile.profileId}>
                    {profile.profileName}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          ) : null}

          {providerProfile?.providerSignatureUrl ? (
            <div className="mb-4 text-center">
              <Image
                src={providerProfile.providerSignatureUrl}
                alt="Provider signature"
                style={{ maxHeight: '120px', maxWidth: '100%' }}
              />
            </div>
          ) : null}

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Examiner's Signature (Name)</Form.Label>
                <Form.Control
                  name="signature"
                  value={certificationData.signature}
                  onChange={handleCertificationChange}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Date Signed</Form.Label>
                <Form.Control
                  type="date"
                  name="dateSigned"
                  value={certificationData.dateSigned}
                  onChange={handleCertificationChange}
                />
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>Examiner's Printed Name and Title</Form.Label>
            <Form.Control
              name="nameAndTitle"
              value={certificationData.nameAndTitle}
              onChange={handleCertificationChange}
              placeholder="e.g., Jane Doe, MD"
            />
          </Form.Group>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Area of Practice/Specialty</Form.Label>
                <Form.Control
                  name="specialty"
                  value={certificationData.specialty}
                  onChange={handleCertificationChange}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Phone/Fax Numbers</Form.Label>
                <Form.Control
                  name="phoneOrFax"
                  value={certificationData.phoneOrFax}
                  onChange={handleCertificationChange}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>National Provider Identifier (NPI)</Form.Label>
                <Form.Control
                  name="npi"
                  value={certificationData.npi}
                  onChange={handleCertificationChange}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Medical License Number and State</Form.Label>
                <Form.Control
                  name="licenseNumberAndState"
                  value={certificationData.licenseNumberAndState}
                  onChange={handleCertificationChange}
                />
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-0">
            <Form.Label>Examiner's Address</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              name="address"
              value={certificationData.address}
              onChange={handleCertificationChange}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => setShowSigningModal(false)}
          >
            <FontAwesomeIcon icon={faTimes} className="me-2" />
            Cancel
          </Button>
          <Button
            onClick={handleSignSubmit}
            disabled={
              signDbqMutation.isPending ||
              !certificationData.nameAndTitle ||
              !certificationData.npi ||
              !certificationData.licenseNumberAndState
            }
          >
            {signDbqMutation.isPending ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                Signing...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faFileSignature} className="me-2" />
                Sign & Submit
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default LegacyUserDbqEditor;
